import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from '../auth/auth.service';
import { Report } from '../models/report.interface';
import { EnterpriseStore } from '../stores/enterprise.store';
import { Enterprise } from '../types/enterprise';
import { DateService } from './date.service';
import { FacilityService } from './facility.service';
import { StorageKeys, StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class EnterpriseService {
  constructor(
    private http: HttpClient,
    private dateService: DateService,
    private storageService: StorageService,
    private enterpriseStore: EnterpriseStore,
    private authService: AuthService,
    private facilityService: FacilityService,
  ) {
    const currentEnterpriseId = this.storageService.getItem(StorageKeys.CURRENT_ENTERPRISE_ID) ?? undefined;
    this.loadEnterprises(currentEnterpriseId).then();
  }

  async loadEnterprises(initEnterpriseId?: string) {
    if (!this.authService.isAuthenticated()) {
      return;
    }

    const enterprises = await this.loadAll();
    this.enterpriseStore.enterprises = enterprises;

    if (initEnterpriseId) {
      const facilities = await this.facilityService.loadAll(initEnterpriseId);
      const enterprise = enterprises.find((ent) => ent.id === initEnterpriseId);
      if (enterprise) {
        enterprise.facilities = (facilities || []).sort((a, b) => a.name.localeCompare(b.name));
        this.enterpriseStore.setCurrentEnterprise(enterprise);
        return;
      }
    }

    if (enterprises.length > 0) {
      const facilities = await this.facilityService.loadAll(enterprises[0].id);
      enterprises[0].facilities = (facilities || []).sort((a, b) => a.name.localeCompare(b.name));
      this.enterpriseStore.setCurrentEnterprise(enterprises[0]);
    }
  }

  setCurrentEnterpriseById(enterpriseId: string): void {
    this.getById(enterpriseId).subscribe((enterprise) => {
      this.enterpriseStore.setCurrentEnterprise(enterprise);
    });
  }

  loadAll(): Promise<Enterprise[]> {
    return this.http.get<Enterprise[]>(environment.apiDomain + '/enterprises/enterprises').toPromise();
  }

  getById(enterpriseId: string): Observable<Enterprise> {
    return this.http.get<Enterprise>(environment.apiDomain + `/enterprises/enterprises/${enterpriseId}`);
  }

  update(enterprise: Enterprise): Observable<Enterprise> {
    return this.http.put<Enterprise>(environment.apiDomain + `/enterprises/enterprises`, enterprise);
  }

  getEnterpriseReport(enterpriseId: string, beginDate: Date, endDate: Date): Observable<Report[]> {
    const beginDateStr = this.dateService.dateToISOFormat(beginDate);
    const endDateStr = this.dateService.dateToISOFormat(endDate);

    return this.http.get<Report[]>(
      `${environment.apiDomain}/ergonomics/enterprises/reports?enterpriseId=${enterpriseId}&dateBegin=${beginDateStr}&dateEnd=${endDateStr}`,
    );
  }

  getCurrentEnterpriseReport(enterpriseId: string): Observable<Report> {
    return this.http.get<Report>(
      `${environment.apiDomain}/ergonomics/enterprises/reports/current?enterpriseId=${enterpriseId}`,
    );
  }
}
